import { onMounted, onUnmounted, ref, type Ref } from 'vue';
import type AiAssistantPanel from '@/components/AiAssistantPanel.vue';
import { useAppShellStore } from '@/stores/useAppShellStore';

export type AiPageQuoteContext = {
  quoted: string;
  fieldPath: string;
  fieldLabel: string;
  positionDescription: string;
  scopeLabel: string;
  editable: boolean;
  cellKey?: string;
};

const QUOTE_SCOPE_ATTR = 'data-ai-quote-scope';
const QUOTE_FIELD_ATTR = 'data-ai-quote-field';
const QUOTE_LABEL_ATTR = 'data-ai-quote-label';
const QUOTE_EDITABLE_ATTR = 'data-ai-quote-editable';
const QUOTE_POSITION_ATTR = 'data-ai-quote-position';
const QUOTE_CELL_KEY_ATTR = 'data-ai-quote-cell-key';

const SKIP_SELECTOR = [
  '.ai-assistant-root',
  '.expanded-ai-overlay',
  '.ai-page-quote-floater',
  '.pc-modify-floater',
  '.mini-toolbar',
  '.mini-comment-toolbar',
  '.document-editor',
  'input',
  'textarea',
  '[contenteditable="true"]',
].join(',');

function findQuoteCellKey(node: Node | null): string {
  let cur: Node | null = node;
  while (cur) {
    if (cur instanceof HTMLElement) {
      const key = cur.getAttribute(QUOTE_CELL_KEY_ATTR)?.trim();
      if (key) return key;
    }
    cur = cur.parentNode;
  }
  return '';
}

function readQuoteContextFromElement(scopeEl: HTMLElement, quoted: string, cellKey?: string): AiPageQuoteContext | null {
  let fieldPath = scopeEl.getAttribute(QUOTE_FIELD_ATTR)?.trim() || '';
  const fieldLabel =
    scopeEl.getAttribute(QUOTE_LABEL_ATTR)?.trim()
    || fieldPath
    || '选中内容';
  let positionDescription = scopeEl.getAttribute(QUOTE_POSITION_ATTR)?.trim() || '';
  let scopeLabel = scopeEl.getAttribute(QUOTE_SCOPE_ATTR)?.trim() || '';
  let parent: HTMLElement | null = scopeEl.parentElement;
  while (parent) {
    if (!scopeLabel) scopeLabel = parent.getAttribute(QUOTE_SCOPE_ATTR)?.trim() || '';
    if (!positionDescription) positionDescription = parent.getAttribute(QUOTE_POSITION_ATTR)?.trim() || '';
    if (scopeLabel && positionDescription) break;
    parent = parent.parentElement;
  }
  if (!fieldPath) fieldPath = scopeLabel || 'selection';
  if (!fieldPath) return null;
  const editableRaw = (scopeEl.getAttribute(QUOTE_EDITABLE_ATTR) || '').trim().toLowerCase();
  const editable = editableRaw === 'true' || editableRaw === '1' || editableRaw === 'yes';
  return {
    quoted,
    fieldPath,
    fieldLabel,
    positionDescription,
    scopeLabel: scopeLabel || '当前页面',
    editable,
    cellKey: cellKey || undefined,
  };
}

function findQuoteScopeElement(node: Node | null, root: ParentNode): HTMLElement | null {
  let cur: Node | null = node;
  while (cur && cur !== root) {
    if (
      cur instanceof HTMLElement
      && (cur.hasAttribute(QUOTE_SCOPE_ATTR) || cur.hasAttribute(QUOTE_FIELD_ATTR))
    ) {
      return cur;
    }
    cur = cur.parentNode;
  }
  return null;
}

function getSelectionViewportRect(range: Range): DOMRect {
  const r = range.getBoundingClientRect();
  if (r.width >= 1 && r.height >= 1) return r;
  const rects = range.getClientRects();
  for (let i = 0; i < rects.length; i += 1) {
    const cr = rects[i] as DOMRect;
    if (cr.width >= 1 && cr.height >= 1) return cr;
  }
  return r;
}

export function useAiPageContentQuote(aiPanelRef: Ref<InstanceType<typeof AiAssistantPanel> | null>) {
  const shellStore = useAppShellStore();
  const floater = ref({
    visible: false,
    top: 0,
    left: 0,
  });
  const pendingContext = ref<AiPageQuoteContext | null>(null);

  function hideFloater() {
    floater.value.visible = false;
    pendingContext.value = null;
  }

  function onDocumentMouseUp() {
    requestAnimationFrame(() => {
      const sel = window.getSelection();
      const text = sel?.toString().trim() ?? '';
      if (!text || !sel || sel.rangeCount === 0 || sel.isCollapsed) {
        hideFloater();
        return;
      }
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      const el =
        node.nodeType === Node.TEXT_NODE
          ? (node.parentElement as HTMLElement | null)
          : (node as HTMLElement);
      if (!el) {
        hideFloater();
        return;
      }
      if (el.closest(SKIP_SELECTOR)) {
        hideFloater();
        return;
      }
      const scopeEl = findQuoteScopeElement(node, document.body);
      if (!scopeEl) {
        hideFloater();
        return;
      }
      const cellKey = findQuoteCellKey(node);
      const ctx = readQuoteContextFromElement(scopeEl, text, cellKey);
      if (!ctx) {
        hideFloater();
        return;
      }
      const rect = getSelectionViewportRect(range);
      pendingContext.value = ctx;
      floater.value = {
        visible: true,
        top: Math.max(8, rect.top - 40),
        left: Math.min(
          Math.max(48, rect.left + rect.width / 2),
          window.innerWidth - 48,
        ),
      };
    });
  }

  function onDocumentPointerDown(e: PointerEvent) {
    const t = e.target as HTMLElement | null;
    if (t?.closest('.ai-page-quote-floater')) return;
    hideFloater();
  }

  function quoteToAi() {
    const ctx = pendingContext.value;
    if (!ctx) return;
    shellStore.setDashboardExpandedAiComposer(false);
    const panel = aiPanelRef.value as {
      beginPageContentEdit?: (args: {
        fieldPath: string;
        fieldLabel: string;
        quoted: string;
        positionDescription: string;
        scopeLabel: string;
        cellKey?: string;
        mode?: 'modify' | 'discuss';
      }) => void;
      expandRightPanel?: () => void;
    } | null;
    panel?.expandRightPanel?.();
    panel?.beginPageContentEdit?.({
      fieldPath: ctx.fieldPath,
      fieldLabel: ctx.fieldLabel,
      quoted: ctx.quoted,
      positionDescription: ctx.positionDescription,
      scopeLabel: ctx.scopeLabel,
      cellKey: ctx.cellKey,
      mode: ctx.editable ? 'modify' : 'discuss',
    });
    window.getSelection()?.removeAllRanges();
    hideFloater();
  }

  onMounted(() => {
    document.addEventListener('mouseup', onDocumentMouseUp);
    document.addEventListener('pointerdown', onDocumentPointerDown, true);
  });

  onUnmounted(() => {
    document.removeEventListener('mouseup', onDocumentMouseUp);
    document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  });

  return {
    floater,
    quoteToAi,
    hideFloater,
  };
}
